import styles from "./CheckoutSection.module.css";
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { createPaymentIntent } from "../../services/OnlineStore.js";
import useCartStore from "../../states/CartStore";
import { LiaCcVisa, LiaCcMastercard, LiaCcAmex, LiaCcDiscover   } from "react-icons/lia";

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const cardBrands = [
    { name: "Visa", icon: <LiaCcVisa size={24}/> },
    { name: "Mastercard", icon: <LiaCcMastercard size={24} /> },
    { name: "Amex", icon: <LiaCcAmex size={24}/> },
    { name: "Discover", icon: <LiaCcDiscover size={24}/> }
];

const Payment = forwardRef(function Payment({ deliveryInfo, billingInfo, onReadyChange, onProcessingChange, onError }, ref) {
    return (
        <div className={styles.payment}>
            <div className={styles.paymentMethods}>
                <div className={styles.cardBrandRow}>
                    <span className={styles.paymentLabel}>Pay with card</span>
                    <div className={styles.brandList}>
                        {cardBrands.map((brand) => (
                            <span key={brand.name} className={styles.brandChip}>
                                <span className={styles.brandName}>{brand.name}</span>
                                {brand.icon}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {stripePromise ? (
                <Elements stripe={stripePromise}>
                    <CreditCardForm
                        ref={ref}
                        deliveryInfo={deliveryInfo}
                        billingInfo={billingInfo}
                        onReadyChange={onReadyChange}
                        onProcessingChange={onProcessingChange}
                        onError={onError}
                    />
                </Elements>
            ) : (
                <p className={styles.paymentUnavailable} role="alert">
                    Card payments are unavailable. Stripe is not configured.
                </p>
            )}
        </div>
    );
});

const CreditCardForm = forwardRef(function CreditCardForm({ deliveryInfo, billingInfo, onReadyChange, onProcessingChange, onError }, ref) {
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState("");
    const [processing, setProcessing] = useState(false);
    const [cardFocused, setCardFocused] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const cart = useCartStore((state) => state.cart);

    // Let the parent step (Checkout) know when the card is fully entered and Stripe is ready to submit
    useEffect(() => {
        onReadyChange?.(Boolean(stripe && elements) && cardComplete);
    }, [stripe, elements, cardComplete, onReadyChange]);

    useEffect(() => {
        onProcessingChange?.(processing);
    }, [processing, onProcessingChange]);

    // Simple hash function for payment_intent_id
    const hashPaymentId = (paymentIntentId) => {
        return btoa(paymentIntentId).substring(0, 16); // Base64 encode and take first 16 chars
    };

    const handleSubmit = async (event) => {
        event?.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
            onError?.("Card element not ready.");
            return;
        }

        setProcessing(true);
        setError("");
        onError?.(null);

        try {
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement,
            });

            if (stripeError) {
                onError?.(stripeError.message || "Payment failed.");
                return;
            }

            const response = await createPaymentIntent(cart.cart_token, deliveryInfo, billingInfo);
            const { payment, customer, order, orderId } = response;
            const clientSecret = payment.client_secret;

            const confirmResult = await stripe.confirmCardPayment(clientSecret, {
                payment_method: paymentMethod.id,
            });

            if (confirmResult.error) {
                navigate("/payment-failure", {
                    state: { message: confirmResult.error.message || "Your payment could not be completed. Please try again." },
                });
                return;
            }

            if (confirmResult.paymentIntent.status === "succeeded") {
                sessionStorage.setItem(
                    "paymentData",
                    JSON.stringify({
                        payment,
                        customer,
                        order,
                        orderId,
                        cartToken: cart.cart_token,
                    })
                );

                const hashedId = hashPaymentId(payment.payment_intent_id);
                navigate(`/checkout/${hashedId}/success`);
            } else {
                navigate("/payment-failure", {
                    state: { message: "Your payment did not complete. Please try again." },
                });
            }
        } catch (err) {
            console.error(err);
            onError?.(err.message || "Something went wrong while processing the payment.");
        } finally {
            setProcessing(false);
        }
    };

    // Exposes submit() so the Pay Now action button can live outside the Elements tree
    useImperativeHandle(ref, () => ({
        submit: () => handleSubmit()
    }));

    return (
        <form onSubmit={handleSubmit} className={styles.creditCardForm}>
            <div className={`${styles.cardElementContainer} ${cardFocused ? styles.cardElementFocused : ""} ${error ? styles.cardElementInvalid : ""}`}>
                <CardElement
                    options={{
                        style: {
                            base: {
                                fontSize: "16px",
                                color: "#000000",
                                fontFamily: "'Schibsted Grotesk', sans-serif",
                                "::placeholder": {
                                    color: "#7a7a7a",
                                },
                            },
                            invalid: {
                                color: "#dc2626",
                                iconColor: "#dc2626",
                            },
                        },
                    }}
                    onFocus={() => setCardFocused(true)}
                    onBlur={() => setCardFocused(false)}
                    onChange={(event) => {
                        setError(event.error ? event.error.message : "");
                        setCardComplete(event.complete);
                    }}
                />
            </div>
            {error && <p className={styles.cardError}>{error}</p>}
        </form>
    );
});

export default Payment;